import { LinearProgress, Paper } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	Severity,
	SPECIFICATION_ALLOWED_MIME_TYPES,
	SPECIFICATION_FILE_SIZE_LIMIT,
} from "node-library/dist/constants";
import {
	generateUniqueFileName,
	getFilenameAndExtension,
} from "node-library/dist/helpers/file_helper";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadAllToS3 } from "../../../helpers/frontendFileOperationHelper";
import SpecificationFilesAndFoldersContents from "../../../pages/mainPages/clientOrganizations/subComponents/specificationFilesAndFoldersContents";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { UploadIcon, DeleteIcon, FolderIcon } from "../../svgIcons";
import { RedErrorIcon, RedCrossIcon } from "../../toast/errorIcons";
import { useSnackbar } from "../../toast/showToastMessage";
import { GreenTickIcon } from "../../toast/successIcons";

interface Props {
	user: FrontendUserInfo;

	testCategory: FrontendCompanyTestCategory;
	selectedMatrix: FrontendCompanySampleMatrix;
	currentFolderSpecifications: Array<FrontendSampleSpecification>;
	setFolderSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;

	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;

	currentFolder: FrontendSampleSpecificationDirectory | undefined;
	setCurrentFolder: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecificationDirectory | undefined>
	>;
	uploadStarted: boolean;
	setUploadStarted: React.Dispatch<React.SetStateAction<boolean>>;
	fileToUpload: {
		file: File;
		progress: number;
	} | null;
	setFileToUpload: React.Dispatch<
		React.SetStateAction<{
			file: File;
			progress: number;
		} | null>
	>;
}

export default function UploadSpecificationFileModalUploader(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		user,
		testCategory,
		selectedMatrix,
		currentFolderSpecifications,
		setFolderSpecifications,
		specDirectories,
		setSpecDirectories,
		currentFolder,
		setCurrentFolder,
		uploadStarted,
		setUploadStarted,
		fileToUpload,
		setFileToUpload,
	} = props;

	const [dragging, setDragging] = useState<boolean>(false);

	// Handle drag enter
	const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(true);
	};

	// Handle drag leave
	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
		const droppedFiles = event.dataTransfer.files;
		if (droppedFiles && droppedFiles.length > 0) {
			validateAndAddFile(droppedFiles);
		}
	};

	// Handle file input change
	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (selectedFiles && selectedFiles.length > 0) {
			validateAndAddFile(selectedFiles);
		}
	};

	function validateAndAddFile(files: FileList) {
		const file = files[0]; // Only take the first file
		if (!file) return;

		const { fileName, extension } = getFilenameAndExtension(file.name);

		if (
			file.size <= SPECIFICATION_FILE_SIZE_LIMIT &&
			SPECIFICATION_ALLOWED_MIME_TYPES.includes(file.type)
		) {
			// Optional: If you still want unique name logic against the current folder
			const existingFiles = currentFolderSpecifications.map((spec) => ({
				fileName: spec.fileName,
				extension: spec.format,
			}));

			const uniqueName = generateUniqueFileName(
				fileName,
				extension,
				existingFiles,
			);
			const newFile = new File([file], uniqueName, { type: file.type });

			setFileToUpload({
				file: newFile,
				progress: 0,
			});
		} else {
			openSnackbar(
				translation("InvalidReportFile"),
				Severity.ERROR,
				translation("InvalidReportFileMsg", { files: file.name }),
				<RedErrorIcon />,
			);
		}
	}
	const handleRemoveFile = () => {
		setFileToUpload(null);
	};

	const specFileFolderSection: React.ReactNode = (
		<SpecificationFilesAndFoldersContents
			user={user}
			orgDetailsSelectedCategory={testCategory}
			orgSpecifications={currentFolderSpecifications}
			setOrgSpecifications={setFolderSpecifications}
			specDirectories={specDirectories}
			selectedMatrix={selectedMatrix}
			currentFolder={currentFolder}
			setCurrentFolder={setCurrentFolder}
			isInsideModal={true}
			setSpecDirectories={setSpecDirectories}
			selectedSpecificationId={undefined}
		/>
	);
	const getBreadcrumbs = () => {
		const crumbs: Array<FrontendSampleSpecificationDirectory> = [];
		let tempFolder = currentFolder;

		while (tempFolder) {
			crumbs.unshift(tempFolder); // Add to the beginning of array
			// Find the parent of the current temp folder
			tempFolder = specDirectories.find(
				(dir) => dir.directoryId === tempFolder?.parentDirectoryId,
			);
		}
		return crumbs;
	};

	const breadcrumbs = getBreadcrumbs();

	const uploadToDirectoryString: string = [
		props.selectedMatrix.subMatrix,
		...breadcrumbs.map((folder) => {
			return folder.name;
		}),
	].join("/");

	const uploader: React.ReactNode = (
		<div>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: "20px",
					marginTop: "20px",
				}}
			>
				<div>
					<Paper
						sx={{
							padding: 3,
							border: dragging
								? `2px dashed ${PrimaryColor.PRIMARY_LV5}`
								: `2px dashed ${NeutralColor.NEUTRAL_GREY_300}`,
							textAlign: "center",
							backgroundColor: "#F3F3F5",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: "none", // Add this line to remove the shadow
						}}
						onDragEnter={handleDragEnter}
						onDragOver={(e) => e.preventDefault()}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
					>
						<div className="test-report-upload-icon-container">
							<UploadIcon></UploadIcon>
						</div>

						<input
							type="file"
							onChange={handleFileChange}
							style={{ display: "none" }}
							id="file-input"
							accept={SPECIFICATION_ALLOWED_MIME_TYPES.join(",")}
						/>
						<div style={{ marginTop: "10px" }}>
							{translation("DragDropSpecFileDescription")}
							<label
								htmlFor="file-input"
								style={{ cursor: "pointer", marginTop: 10 }}
							>
								<div>
									<b>
										<u>{translation("chooseFile")}</u>
									</b>
								</div>
							</label>
						</div>
					</Paper>
					{/* Display the uploaded files */}
					<div style={{ marginTop: 3, maxHeight: "500px", overflow: "auto" }}>
						{fileToUpload &&
							[fileToUpload].map((file, index) => {
								let rowIcon: React.ReactNode;
								if (file.progress == -1) {
									rowIcon = <RedCrossIcon></RedCrossIcon>;
								} else if (file.progress == 100) {
									rowIcon = (
										<GreenTickIcon
											size={20}
											strokeColor="white"
											fillColor={SemanticColor.SUCCESS}
										></GreenTickIcon>
									);
								} else if (!uploadStarted) {
									rowIcon = (
										<div
											style={{ cursor: "pointer" }}
											onClick={() => {
												handleRemoveFile();
											}}
										>
											<DeleteIcon></DeleteIcon>
										</div>
									);
								} else {
									rowIcon = <div></div>;
								}

								return (
									<>
										<div className="upload-report-file-row" key={index}>
											<div style={{ maxWidth: "80%", wordWrap: "break-word" }}>
												{file.file.name}
											</div>
											{rowIcon}
										</div>
										<LinearProgress
											sx={{
												borderRadius: "4px",
												backgroundColor: NeutralColor.NEUTRAL_GREY_100,
												"& .MuiLinearProgress-bar": {
													backgroundColor:
														file.progress === 100
															? SemanticColor.SUCCESS
															: SecondaryColor.SECONDARY_LV10,
												},
												"& .MuiLinearProgress-bar1Determinate": {
													animationDuration: "0.3s", // Adjust this value to speed up or slow down the animation
												},
												"& .MuiLinearProgress-determinate": {
													animationDuration: "0.3s", // Speed up the indeterminate animation by changing this duration
												},
											}}
											variant="determinate"
											value={file.progress}
										/>
									</>
								);
							})}
					</div>
					<div style={{ marginTop: "10px" }}>
						<span
							style={{
								padding: "8px",
								borderRadius: "6px",
								backgroundColor: SecondaryColor.SECONDARY_200,
							}}
						>
							{`${translation("UploadingFileTo")}: ${uploadToDirectoryString}`}
						</span>
					</div>
				</div>

				<div>
					<div
						className="typography-caption"
						style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					>
						{translation("MatrixSubmatrixFolder")}
					</div>
					<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
						<FolderIcon
							strokeColor={SecondaryColor.SECONDARY_300}
							fillColor={SecondaryColor.SECONDARY_LV10}
						/>
						{props.selectedMatrix.subMatrix}
					</div>

					{specFileFolderSection}
				</div>
			</div>
		</div>
	);

	return <>{uploader}</>;
}
